'use client';

import { useSearchParams } from 'next/navigation';
import React from 'react';
import { MdViewModule } from 'react-icons/md';
import Jazzicon, { jsNumberForAddress } from 'react-jazzicon';

import useQueryWithPages from 'src/hooks/useQueryWithPages';

import BigNumber from 'bignumber.js';

import { formatTimestamp } from '../../utils/formatTimestamp';
import { formatNumber } from '../../utils/formatNumber';
import styles from './BlocksPage.module.css';

const formatWei = (value: string | null | undefined) => {
  if (!value || value === '0') return '0';
  const bn = BigNumber(value);
  const eth = bn.dividedBy(BigNumber(10).pow(18));
  return eth.toFixed(6);
};

const BlocksPage: React.FC = () => {
  const searchParams = useSearchParams();
  const tab = searchParams?.get('tab') || 'blocks';

  const blocksQuery = useQueryWithPages({
    resourceName: 'blocks',
    filters: { type: 'block' },
    options: {
      enabled: tab === 'blocks',
    },
  });

  const reorgsQuery = useQueryWithPages({
    resourceName: 'blocks',
    filters: { type: 'reorg' },
    options: {
      enabled: tab === 'reorgs',
    },
  });

  const unclesQuery = useQueryWithPages({
    resourceName: 'blocks',
    filters: { type: 'uncle' },
    options: {
      enabled: tab === 'uncles',
    },
  });

  const currentQuery = tab === 'reorgs' ? reorgsQuery : tab === 'uncles' ? unclesQuery : blocksQuery;
  const blocks = React.useMemo(() => {
    const items = currentQuery.data?.items || [];
    // Sort by height descending (newest first)
    return [ ...items ].sort((a, b) => {
      if (a.height && b.height) {
        return b.height - a.height;
      }
      return 0;
    });
  }, [ currentQuery.data?.items ]);

  return (
    <div className={ styles.container }>
      { /* Header */ }
      <h1 className={ styles.title }>Blocks</h1>

      { /* Tabs and Controls */ }
      <div className={ styles.controls }>
        <div className={ styles.tabs }>
          <a
            href="?tab=blocks"
            className={ `${ styles.tab } ${ tab === 'blocks' || !tab ? styles.tabActive : '' }` }
          >
            All
          </a>
          <a
            href="?tab=reorgs"
            className={ `${ styles.tab } ${ tab === 'reorgs' ? styles.tabActive : '' }` }
          >
            Forked
          </a>
          <a
            href="?tab=uncles"
            className={ `${ styles.tab } ${ tab === 'uncles' ? styles.tabActive : '' }` }
          >
            Uncles
          </a>
        </div>
        <div className={ styles.rightControls }>
          <div className={ styles.info }>
            Network utilization (last 50 blocks): <span className={ styles.infoValue }>0.12%</span>
          </div>
          <div className={ styles.info }>
            <span className={ styles.infoIcon }>⏱</span> Block countdown
          </div>
          <div className={ styles.pagination }>
            <button
              className={ styles.pageButton }
              disabled={ !currentQuery.pagination.canGoBackwards }
              onClick={ currentQuery.pagination.onPrevPageClick }
            >
              First
            </button>
            <button
              className={ styles.pageButton }
              disabled={ !currentQuery.pagination.canGoBackwards }
              onClick={ currentQuery.pagination.onPrevPageClick }
            >
              &lt;
            </button>
            <button className={ `${ styles.pageButton } ${ styles.pageButtonActive }` }>
              { currentQuery.pagination.page }
            </button>
            <button
              className={ styles.pageButton }
              disabled={ !currentQuery.pagination.hasNextPage }
              onClick={ currentQuery.pagination.onNextPageClick }
            >
              &gt;
            </button>
          </div>
        </div>
      </div>

      { /* Table */ }
      <div className={ styles.tableContainer }>
        <table className={ styles.table }>
          <thead>
            <tr>
              <th>Block</th>
              <th>Size, bytes</th>
              <th>Validator</th>
              <th>Txn</th>
              <th>Gas used</th>
              <th>Reward OGPU</th>
              <th>Burnt fees OGPU</th>
            </tr>
          </thead>
          <tbody>
            { currentQuery.isLoading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <tr key={ i }>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div className={ styles.skeleton } style={{ width: '4rem', height: '0.875rem', display: 'block' }} />
                      <div className={ styles.skeleton } style={{ width: '5rem', height: '0.75rem', display: 'block' }} />
                    </div>
                  </td>
                  <td><div className={ styles.skeleton } style={{ width: '5rem', height: '0.875rem' }} /></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', minWidth: 0 }}>
                      <div className={ styles.skeleton } style={{ width: '1rem', height: '1rem', borderRadius: '50%', flexShrink: 0 }} />
                      <div className={ styles.skeleton } style={{ width: '7rem', height: '0.875rem' }} />
                    </div>
                  </td>
                  <td><div className={ styles.skeleton } style={{ width: '2rem', height: '0.875rem' }} /></td>
                  <td><div className={ styles.skeleton } style={{ width: '4rem', height: '0.875rem' }} /></td>
                  <td><div className={ styles.skeleton } style={{ width: '4rem', height: '0.875rem' }} /></td>
                  <td><div className={ styles.skeleton } style={{ width: '3rem', height: '0.875rem' }} /></td>
                </tr>
              ))
            ) : blocks.length === 0 ? (
              <tr>
                <td colSpan={ 7 }>
                  <div className={ styles.emptyState }>
                    <MdViewModule size={ 48 }/>
                    <p>There are no blocks.</p>
                  </div>
                </td>
              </tr>
            ) : (
              blocks.map((block, index: number) => (
                <tr key={ block.hash || index }>
                  <td>
                    <a href={ `/block/${ block.height }` } className={ styles.link }>
                      { formatNumber(block.height) }
                    </a>
                    <div className={ styles.age }>{ formatTimestamp(block.timestamp) }</div>
                  </td>
                  <td>{ formatNumber(block.size) } bytes</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                      { block.miner?.hash && (
                        <span style={{ flexShrink: 0, display: 'inline-flex' }}>
                          <Jazzicon diameter={ 16 } seed={ jsNumberForAddress(block.miner.hash) } />
                        </span>
                      ) }
                      <a href={ `/address/${ block.miner?.hash }` } className={ styles.address }>
                        { block.miner?.hash?.slice(0, 8) }...{ block.miner?.hash?.slice(-6) }
                      </a>
                    </div>
                  </td>
                  <td>{ formatNumber(block.transaction_count) }</td>
                  <td>{ formatNumber(block.gas_used) }</td>
                  <td className={ styles.value }>{ formatWei(block.rewards?.[ 0 ]?.reward) }</td>
                  <td className={ styles.fee }>{ formatWei(block.burnt_fees) }</td>
                </tr>
              ))
            ) }
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BlocksPage;
